import React, { useMemo, type ReactNode } from "react";
import {
  ProvetBookingFlowProvider,
  useProvetBookingFlow as useBookingFlowContext
} from "./provetBookingFlowContext";
import { ProvetDataProvider, useProvetData } from "./provetDataContext";
import { ProvetStaticProvider, useProvetStatic } from "./provetStaticContext";

/**
 * Composite Provet context type for backward compatibility.
 * Components should prefer using specific context hooks (useProvetStatic, useProvetData, useProvetBookingFlow)
 * to avoid unnecessary re-renders.
 */
interface ProvetContextType {
  // From ProvetStaticContext
  api: ReturnType<typeof useProvetStatic>["api"];
  language: string;
  // From ProvetDataContext
  departments: ReturnType<typeof useProvetData>["departments"];
  departmentsStatus: ReturnType<typeof useProvetData>["departmentsStatus"];
  reloadDepartments: ReturnType<typeof useProvetData>["reloadDepartments"];
  reasonGroups: ReturnType<typeof useProvetData>["reasonGroups"];
  reasons: ReturnType<typeof useProvetData>["reasons"];
  reasonsStatus: ReturnType<typeof useProvetData>["reasonsStatus"];
  reloadReasons: ReturnType<typeof useProvetData>["reloadReasons"];
  visitedClinicBookingProvetId?: ReturnType<
    typeof useProvetData
  >["visitedClinicBookingProvetId"];
  setVisitedClinicBookingProvetId: ReturnType<
    typeof useProvetData
  >["setVisitedClinicBookingProvetId"];
  visitedTreatmentProvetId?: ReturnType<typeof useProvetData>["visitedTreatmentProvetId"];
  setVisitedTreatmentProvetId: ReturnType<
    typeof useProvetData
  >["setVisitedTreatmentProvetId"];
  // From ProvetBookingFlowContext
  bookingFlowState: ReturnType<typeof useBookingFlowContext>["bookingFlowState"];
  bookingFlowDispatch: ReturnType<typeof useBookingFlowContext>["bookingFlowDispatch"];
}

interface ProvetContextProviderProps {
  children: ReactNode;
  sessionId: string;
  antiForgeryToken?: string;
  activeDepartments: number[] | undefined;
  excludedProvetDepartments: number[] | undefined;
  visitedClinicBookingProvetId?: number;
  visitedTreatmentProvetId?: number;
  language: string;
}

/**
 * Composite provider that wraps all three Provet contexts.
 * Maintains backward compatibility while enabling granular context access.
 */
export const ProvetContextProvider = ({
  children,
  sessionId,
  antiForgeryToken,
  activeDepartments,
  excludedProvetDepartments,
  visitedClinicBookingProvetId,
  visitedTreatmentProvetId,
  language
}: ProvetContextProviderProps) => (
  <ProvetStaticProvider
    language={language}
    antiForgeryToken={antiForgeryToken}
    activeDepartmentIds={activeDepartments || []}
    excludedDepartmentIds={excludedProvetDepartments || []}
  >
    <ProvetDataProvider
      visitedClinicBookingProvetId={visitedClinicBookingProvetId}
      visitedTreatmentProvetId={visitedTreatmentProvetId}
    >
      <ProvetBookingFlowProvider sessionId={sessionId}>
        {children}
      </ProvetBookingFlowProvider>
    </ProvetDataProvider>
  </ProvetStaticProvider>
);

/**
 * Hook that combines all three contexts for backward compatibility.
 *
 * ?? WARNING: This hook will cause re-renders whenever ANY context changes.
 * Prefer using specific hooks instead:
 * - useProvetStatic() - for api, language
 * - useProvetData() - for departments, reasons, reasonGroups
 * - useProvetBookingFlow() - for bookingFlowState, dispatch
 */
export const useProvetContext = (): ProvetContextType => {
  const staticContext = useProvetStatic();
  const dataContext = useProvetData();
  const bookingFlowContext = useBookingFlowContext();

  // Memoize the combined context to prevent unnecessary object creation
  const combinedContext = useMemo(
    () => ({
      ...staticContext,
      ...dataContext,
      ...bookingFlowContext
    }),
    [staticContext, dataContext, bookingFlowContext]
  );

  return combinedContext;
};

export type { ProvetContextType };
